/**
 * PersistError values (W1-13 persist-workers; contract persist/types.ts `PersistError`).
 *
 * Every persist call rejects with a `PersistFailure`: an Error (so it carries a stack and can be thrown) that is
 * also a `PersistError` `{ code, message }`. Across the worker boundary the RPC layer keeps `code`, and the client
 * turns the remote error back into a `PersistFailure` with the same code.
 */

import type { PersistError } from './types';

export type PersistErrorCode = PersistError['code'];

const CODES: readonly PersistErrorCode[] = ['lockedElsewhere', 'lockLost', 'notFound', 'quota', 'io'];

export class PersistFailure extends Error implements PersistError {
  override readonly name = 'PersistError';
  readonly code: PersistErrorCode;
  constructor(code: PersistErrorCode, message: string, options?: { readonly cause?: unknown }) {
    super(message, options);
    this.code = code;
  }
}

export function isPersistErrorCode(x: unknown): x is PersistErrorCode {
  return typeof x === 'string' && (CODES as readonly string[]).includes(x);
}

/** Error name of a DOMException-like value (or ''). */
function errorName(e: unknown): string {
  if (typeof e === 'object' && e !== null) {
    const name = (e as { name?: unknown }).name;
    if (typeof name === 'string') return name;
  }
  return '';
}

/** True for storage-full failures (IndexedDB transaction aborts, OPFS writes). */
export function isQuotaError(e: unknown): boolean {
  if (e instanceof PersistFailure) return e.code === 'quota';
  const name = errorName(e);
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  if (typeof e === 'object' && e !== null) {
    const inner = (e as { error?: unknown; cause?: unknown }).error ?? (e as { cause?: unknown }).cause;
    if (inner !== undefined && inner !== e) return isQuotaError(inner);
  }
  return false;
}

/** Human-readable one-line description of any thrown value. */
export function describeError(e: unknown): string {
  if (e instanceof Error) return e.message === '' ? e.name : e.message;
  if (typeof e === 'object' && e !== null) {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return String(e);
}

/** Normalises any failure to a PersistFailure (quota errors keep their code; other errors become `fallback`). */
export function toPersistFailure(e: unknown, fallback: PersistErrorCode = 'io'): PersistFailure {
  if (e instanceof PersistFailure) return e;
  if (typeof e === 'object' && e !== null) {
    const code = (e as { code?: unknown }).code;
    if (isPersistErrorCode(code)) return new PersistFailure(code, describeError(e), { cause: e });
  }
  if (isQuotaError(e)) return new PersistFailure('quota', describeError(e), { cause: e });
  return new PersistFailure(fallback, describeError(e), { cause: e });
}

/** `"<code>: <message>"`, the format of `StorageHealth.lastWriteError`. */
export function writeErrorText(e: unknown): string {
  const f = toPersistFailure(e);
  return `${f.code}: ${f.message}`;
}
